import React, { useEffect, useRef, forwardRef } from "react";
import Quill from "quill";
import "quill/dist/quill.snow.css";

interface EditorProps {
  defaultValue?: string;
  placeholder?: string;
  onChange: (value: string) => void;
}

const Editor = forwardRef<HTMLDivElement, EditorProps>(({
  defaultValue = "",
  placeholder = "Write your feedback...",
  onChange,
}, ref) => {
  const editorRef = useRef<HTMLDivElement | null>(null);
  const quillInstance = useRef<Quill | null>(null);

  useEffect(() => {
    if (!editorRef.current || quillInstance.current) return;

    quillInstance.current = new Quill(editorRef.current, {
      theme: "snow",
      placeholder: placeholder,
      modules: {
        toolbar: [
          [{ header: [1, 2, false] }],
          ["bold", "italic", "underline"],
          ["blockquote", "code-block"],
          [{ list: "ordered" }, { list: "bullet" }],
          [{ script: "sub" }, { script: "super" }],
          [{ indent: "-1" }, { indent: "+1" }],
          [{ direction: "rtl" }],
          [{ size: ["small", false, "large", "huge"] }],
          [{ color: [] }, { background: [] }],
          [{ font: [] }],
          [{ align: [] }],
          ["clean"],
        ],
      },
    });

    quillInstance.current.root.innerHTML = defaultValue;

    const handleTextChange = () => {
      const value = quillInstance.current!.root.innerHTML;
      onChange(value);
    };

    quillInstance.current.on("text-change", handleTextChange);

    return () => {
      if (quillInstance.current) {
        quillInstance.current.off("text-change", handleTextChange);
        quillInstance.current = null;
      }
    };
  }, []);

  return (
    <div>
      <div ref={(node) => {
        editorRef.current = node;
        if (typeof ref === 'function') {
          ref(node);
        } else if (ref) {
          ref.current = node;
        }
      }} />
    </div>
  );
});

Editor.displayName = 'Editor';

export default Editor;
